import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchool } from '../contexts/SchoolContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useDishes, useCreateDish, useDeleteDish } from '../hooks/useDishes';
import toast from 'react-hot-toast';

const DishesPage = () => {
  const { t } = useTranslation();
  const { school } = useSchool();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDishName, setNewDishName] = useState('');
  
  // Use React Query hooks for data fetching
  const { 
    data: dishes = [], 
    isLoading, 
    error 
  } = useDishes(school?.id);
  
  // Use React Query hooks for mutations
  const createDishMutation = useCreateDish();
  const deleteDishMutation = useDeleteDish();
  
  const handleCreateDish = async (e) => {
    e.preventDefault();
    if (!newDishName.trim()) return;
    
    try {
      await createDishMutation.mutateAsync({
        schoolId: school.id,
        name: newDishName.trim(),
        translations: { [t('language.code', 'en')]: newDishName.trim() }
      });
      
      // Clear form and hide it on success
      setNewDishName('');
      setShowAddForm(false);
      toast.success(t('dishes.addSuccess', 'Dish added successfully'));
    } catch (err) {
      toast.error(t('dishes.addError', 'Failed to add dish'));
      console.error('Error creating dish:', err);
    }
  };
  
  const handleDeleteDish = async (id) => {
    if (!window.confirm(t('dishes.confirmDelete', 'Are you sure you want to delete this dish?'))) {
      return;
    }
    
    try {
      await deleteDishMutation.mutateAsync({
        id,
        schoolId: school.id
      });
      toast.success(t('dishes.deleteSuccess', 'Dish deleted successfully'));
    } catch (err) {
      toast.error(t('dishes.deleteError', 'Failed to delete dish'));
      console.error('Error deleting dish:', err);
    }
  };
  
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
          <p>{t('dishes.error', 'Error loading dishes')}: {error.message}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('dishes.title', 'School Dishes')}</h1>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow"
        >
          {showAddForm ? t('common.cancel', 'Cancel') : t('dishes.addNew', 'Add New Dish')}
        </button>
      </div>
      
      {showAddForm && (
        <div className="mb-6 p-4 border rounded-md bg-gray-50">
          <h2 className="text-lg font-semibold mb-4">{t('dishes.addNew', 'Add New Dish')}</h2>
          <form onSubmit={handleCreateDish}>
            <div className="mb-4">
              <label htmlFor="dishName" className="block text-sm font-medium text-gray-700 mb-1">
                {t('dishes.name', 'Dish Name')}
              </label>
              <input
                type="text"
                id="dishName"
                value={newDishName}
                onChange={(e) => setNewDishName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder={t('dishes.namePlaceholder', 'Enter dish name')}
                required
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow"
                disabled={createDishMutation.isLoading}
              >
                {createDishMutation.isLoading ? 
                  t('common.saving', 'Saving...') : 
                  t('common.save', 'Save')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {dishes.length === 0 ? (
        <p>{t('dishes.empty', 'No dishes found. Start by adding your first dish.')}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {dishes.map(dish => (
            <div key={dish.id} className="border rounded-md p-4 shadow-sm">
              <div className="flex justify-between">
                <h3 className="font-semibold">{dish.name}</h3>
                <button
                  onClick={() => handleDeleteDish(dish.id)}
                  className="text-red-500 hover:text-red-700"
                  aria-label={t('common.delete', 'Delete')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 112 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              {dish.description && <p className="text-gray-600">{dish.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DishesPage; 